import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/TextLayer.css"
import "react-pdf/dist/Page/AnnotationLayer.css"
import styled from "styled-components"
import ZoomInIcon from "@mui/icons-material/ZoomIn"
import ZoomOutIcon from "@mui/icons-material/ZoomOut"
import SearchIcon from "@mui/icons-material/Search"
import CloseIcon from "@mui/icons-material/Close"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import RotateRightIcon from "@mui/icons-material/RotateRight"
import DownloadIcon from "@mui/icons-material/Download"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import BrokerStatementLoader from "~components/portfolio/loaders/BrokerStatementLoader"
import useProofOfOwnershipFile from "~hooks/useProofOfOwnershipFile"
import colors from "~constants/colors"

// pdf.js needs a web worker; Vite bundles it from the dist build.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString()

export interface Statement {
  item: ProofOfOwnership
  isLoadingList: boolean
}

const MIN_SCALE = 0.5
const MAX_SCALE = 3
const SCALE_STEP = 0.2
const PAGE_GUTTER = 24

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"]

const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"]/g,
    char =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
      })[char] || char,
  )

const guessIsImageFromUrl = (url: string) => {
  const clean = url.split("?")[0].toLowerCase()
  return IMAGE_EXTENSIONS.some(ext => clean.endsWith(`.${ext}`))
}

// Presigned S3 URLs are served without CORS headers. The PDF viewer must read
// the bytes via fetch, which the browser blocks cross-origin. In dev we route
// the fetch through the Vite proxy (see vite.config.mts) so it comes back
// same-origin. In production the bucket must expose CORS for the app origin.
const withFileProxy = (url: string) =>
  import.meta.env.DEV ? `/__poo_file?url=${encodeURIComponent(url)}` : url

const ProofOfOwnershipViewer = ({ item: poo }: Statement) => {
  const fileId = poo?.file_id || poo?.id || ""
  const pooId = poo?.id || ""

  const {
    data: file,
    isFetching: isFetchingPooData,
    isError,
  } = useProofOfOwnershipFile(fileId, pooId)

  const fileUrl = file?.url

  // The presigned URL may be signed with `Content-Disposition: attachment`,
  // which makes the browser download the file. Fetch the bytes and serve them
  // through a blob URL so react-pdf can render them, and learn the content type
  // so images are rendered as <img> instead of being handed to the PDF engine.
  // Falls back to the raw URL if the fetch fails (e.g. CORS).
  const [blobUrl, setBlobUrl] = useState<string>("")
  const [isResolvingBlob, setIsResolvingBlob] = useState<boolean>(false)
  const [isImage, setIsImage] = useState<boolean>(false)

  useEffect(() => {
    if (!fileUrl) {
      setBlobUrl("")
      setIsResolvingBlob(false)
      setIsImage(false)
      return
    }

    let objectUrl = ""
    let cancelled = false
    setIsResolvingBlob(true)
    setBlobUrl("")
    setIsImage(guessIsImageFromUrl(fileUrl))

    fetch(withFileProxy(fileUrl))
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`)
        return res.blob()
      })
      .then(blob => {
        if (cancelled) return
        const blobIsImage = blob.type?.startsWith("image/") ?? false
        setIsImage(blobIsImage)
        const inlineBlob = blobIsImage
          ? blob
          : new Blob([blob], { type: "application/pdf" })
        objectUrl = URL.createObjectURL(inlineBlob)
        setBlobUrl(objectUrl)
      })
      .catch(() => {
        // Fallback to the raw presigned URL; keep the extension-based guess.
        if (!cancelled) setBlobUrl("")
      })
      .finally(() => {
        if (!cancelled) setIsResolvingBlob(false)
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [fileUrl])

  const viewerSrc = blobUrl || fileUrl || ""
  const isLoading =
    isFetchingPooData || isResolvingBlob || (!fileUrl && !isError)

  // --- PDF rendering / navigation state ---
  const scrollRef = useRef<HTMLDivElement>(null)
  const [numPages, setNumPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [scale, setScale] = useState<number>(1)
  const [rotation, setRotation] = useState<number>(0)
  const [containerWidth, setContainerWidth] = useState<number>(0)

  // --- Search state ---
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [matchCount, setMatchCount] = useState<number>(0)
  const [activeMatch, setActiveMatch] = useState<number>(0)
  const matchNodesRef = useRef<HTMLElement[]>([])

  // Reset per-document state when the source changes.
  useEffect(() => {
    setNumPages(0)
    setCurrentPage(1)
    setScale(1)
    setRotation(0)
    setSearchTerm("")
    setMatchCount(0)
    setActiveMatch(0)
    matchNodesRef.current = []
  }, [viewerSrc])

  // Track the container width so pages can render at a fit-to-width size.
  useLayoutEffect(() => {
    const node = scrollRef.current
    if (!node) return
    const update = () => setContainerWidth(node.clientWidth)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [isLoading, isImage])

  const pageWidth = useMemo(() => {
    if (!containerWidth) return undefined
    return Math.max(0, containerWidth - PAGE_GUTTER * 2) * scale
  }, [containerWidth, scale])

  const handleDocumentLoad = useCallback(
    ({ numPages: total }: { numPages: number }) => {
      setNumPages(total)
    },
    [],
  )

  // Highlight every occurrence of the search term inside each text item.
  const textRenderer = useCallback(
    (textItem: { str: string }) => {
      const { str } = textItem
      const term = searchTerm.trim()
      if (!term) return escapeHtml(str)

      const lower = str.toLowerCase()
      const needle = term.toLowerCase()
      let result = ""
      let cursor = 0
      let idx = lower.indexOf(needle, cursor)
      while (idx !== -1) {
        result += escapeHtml(str.slice(cursor, idx))
        result += `<mark class="poo-search-hit">${escapeHtml(
          str.slice(idx, idx + needle.length),
        )}</mark>`
        cursor = idx + needle.length
        idx = lower.indexOf(needle, cursor)
      }
      result += escapeHtml(str.slice(cursor))
      return result
    },
    [searchTerm],
  )

  // Collect the highlighted nodes (in document/reading order) so we can count
  // and navigate between matches.
  const refreshMatches = useCallback(() => {
    const root = scrollRef.current
    if (!root) return
    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>("mark.poo-search-hit"),
    )
    matchNodesRef.current = nodes
    setMatchCount(nodes.length)
    setActiveMatch(prev => {
      if (nodes.length === 0) return 0
      return Math.min(prev, nodes.length - 1)
    })
  }, [])

  // Re-collect after the term changes or pages re-render the text layer.
  useEffect(() => {
    setActiveMatch(0)
    const timer = window.setTimeout(refreshMatches, 120)
    return () => window.clearTimeout(timer)
  }, [searchTerm, numPages, scale, refreshMatches])

  // Move the "active" highlight and scroll it into view.
  useEffect(() => {
    const nodes = matchNodesRef.current
    nodes.forEach((node, i) =>
      node.classList.toggle("poo-search-hit--active", i === activeMatch),
    )
    const node = nodes[activeMatch]
    if (node) {
      node.scrollIntoView({ block: "center", behavior: "smooth" })
    }
  }, [activeMatch, matchCount])

  const goToMatch = useCallback((delta: number) => {
    const total = matchNodesRef.current.length
    if (total === 0) return
    setActiveMatch(prev => (prev + delta + total) % total)
  }, [])

  // Track which page is currently in view for the page indicator.
  const handleScroll = useCallback(() => {
    const root = scrollRef.current
    if (!root) return
    const pages = root.querySelectorAll<HTMLElement>("[data-page-number]")
    const viewTop = root.scrollTop
    let closest = 1
    let closestDistance = Infinity
    pages.forEach(page => {
      const distance = Math.abs(page.offsetTop - viewTop)
      if (distance < closestDistance) {
        closestDistance = distance
        closest = Number(page.dataset.pageNumber) || closest
      }
    })
    setCurrentPage(closest)
  }, [])

  const zoomIn = () =>
    setScale(s => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2)))
  const zoomOut = () =>
    setScale(s => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2)))
  const rotateClockwise = () => setRotation(r => (r + 90) % 360)

  const handleDownload = useCallback(() => {
    if (!viewerSrc) return
    const link = document.createElement("a")
    link.href = viewerSrc
    link.download = poo?.name || "proof-of-ownership"
    document.body.appendChild(link)
    link.click()
    link.remove()
  }, [viewerSrc, poo?.name])

  const renderToolbar = () => (
    <Toolbar>
      <ToolGroup>
        <ToolButton
          onClick={zoomOut}
          disabled={scale <= MIN_SCALE}
          title="Zoom out"
        >
          <ZoomOutIcon fontSize="small" />
        </ToolButton>
        <ZoomLabel>{Math.round(scale * 100)}%</ZoomLabel>
        <ToolButton
          onClick={zoomIn}
          disabled={scale >= MAX_SCALE}
          title="Zoom in"
        >
          <ZoomInIcon fontSize="small" />
        </ToolButton>
        {numPages > 0 && (
          <PageLabel>
            {currentPage} / {numPages}
          </PageLabel>
        )}
        <Divider />
        <ToolButton onClick={rotateClockwise} title="Rotate clockwise">
          <RotateRightIcon fontSize="small" />
        </ToolButton>
        <ToolButton onClick={handleDownload} title="Download">
          <DownloadIcon fontSize="small" />
        </ToolButton>
      </ToolGroup>

      <SearchBox>
        <SearchIcon fontSize="small" style={{ color: colors.gray6 }} />
        <SearchInput
          value={searchTerm}
          placeholder="Search in document"
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault()
              goToMatch(e.shiftKey ? -1 : 1)
            }
          }}
        />
        {searchTerm.trim() && (
          <MatchCount>
            {matchCount > 0 ? `${activeMatch + 1}/${matchCount}` : "0/0"}
          </MatchCount>
        )}
        <ToolButton
          onClick={() => goToMatch(-1)}
          disabled={matchCount === 0}
          title="Previous match"
        >
          <KeyboardArrowUpIcon fontSize="small" />
        </ToolButton>
        <ToolButton
          onClick={() => goToMatch(1)}
          disabled={matchCount === 0}
          title="Next match"
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </ToolButton>
        {searchTerm && (
          <ToolButton onClick={() => setSearchTerm("")} title="Clear search">
            <CloseIcon fontSize="small" />
          </ToolButton>
        )}
      </SearchBox>
    </Toolbar>
  )

  return (
    <ViewerContainer>
      {isLoading ? (
        <LoaderFallback>
          <BrokerStatementLoader />
        </LoaderFallback>
      ) : isError || !viewerSrc ? (
        <LoaderFallback>
          <span>Failed to load file</span>
        </LoaderFallback>
      ) : isImage ? (
        <ImageScroll ref={scrollRef}>
          <ImageEl src={viewerSrc} alt="Proof of ownership" />
        </ImageScroll>
      ) : (
        <>
          {renderToolbar()}
          <PdfScroll ref={scrollRef} onScroll={handleScroll}>
            <Document
              file={viewerSrc}
              onLoadSuccess={handleDocumentLoad}
              loading={
                <LoaderFallback>
                  <BrokerStatementLoader />
                </LoaderFallback>
              }
              error={
                <LoaderFallback>
                  <span>Failed to load file</span>
                </LoaderFallback>
              }
            >
              {Array.from({ length: numPages }, (_, index) => (
                <PageWrapper key={`page_${index + 1}`}>
                  <Page
                    pageNumber={index + 1}
                    width={pageWidth}
                    rotate={rotation}
                    customTextRenderer={textRenderer}
                    onRenderTextLayerSuccess={refreshMatches}
                    renderAnnotationLayer={false}
                  />
                </PageWrapper>
              ))}
            </Document>
          </PdfScroll>
        </>
      )}
    </ViewerContainer>
  )
}

const ViewerContainer = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.gray2};
  border-radius: inherit;
  overflow: hidden;
  background: ${colors.white};
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 10px;
  border-bottom: 1px solid ${colors.gray2};
  background: ${colors.white};
  flex-wrap: wrap;
`

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Divider = styled.span`
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: ${colors.gray2};
`

const ToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${colors.blue3};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.gray2};
  }

  &:disabled {
    color: ${colors.gray6};
    opacity: 0.4;
    cursor: default;
  }
`

const ZoomLabel = styled.span`
  min-width: 42px;
  text-align: center;
  font-size: 12px;
  color: ${colors.blue3};
`

const PageLabel = styled.span`
  margin-left: 8px;
  font-size: 12px;
  color: ${colors.gray6};
`

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  border: 1px solid ${colors.gray2};
  border-radius: 8px;
  background: ${colors.white};
`

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 13px;
  width: 160px;
  background: transparent;
  color: ${colors.blue3};
`

const MatchCount = styled.span`
  font-size: 12px;
  color: ${colors.gray6};
  white-space: nowrap;
  padding: 0 4px;
`

const PdfScroll = styled.div`
  flex: 1;
  min-height: 400px;
  overflow: auto;
  background: ${colors.gray2};
  /* Block layout (not flex centering): a zoomed page wider than the viewport is
     centered via the page's auto margins when it fits, and left-aligned —
     reachable by scrolling in BOTH directions — when it overflows. Flex
     align-items:center would push the left edge into unreachable scroll space.
     Horizontal padding keeps a gutter that survives the overflow scroll. */
  padding: ${PAGE_GUTTER}px;

  .react-pdf__Page {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    background: ${colors.white};
  }

  mark.poo-search-hit {
    background: rgba(255, 213, 0, 0.45);
    color: inherit;
    padding: 0;
  }

  mark.poo-search-hit--active {
    background: rgba(255, 145, 0, 0.7);
  }
`

const PageWrapper = styled.div`
  /* Shrink-wrap the page, then center with auto margins. When the page is wider
     than the viewport the margins collapse to 0, leaving it left-aligned and
     fully scrollable instead of clipped. */
  width: max-content;
  margin: 0 auto ${PAGE_GUTTER}px;
`

const ImageScroll = styled.div`
  flex: 1;
  min-height: 400px;
  overflow: auto;
  background: ${colors.gray2};
  padding: ${PAGE_GUTTER}px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const ImageEl = styled.img`
  max-width: 100%;
  height: auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  background: ${colors.white};
`

const LoaderFallback = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.gray6};
  font-size: 13px;
`

export default ProofOfOwnershipViewer
